import { Link, useMatches } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useState } from "react";

import { Soon } from "../../../ui/Soon";
import shell from "../studio2.module.css";
import { CreatePicker } from "./CreatePicker";
import { GettingStarted } from "./GettingStarted";
import classes from "./simple-nav.module.css";
import { SIMPLE_NAV, type SimpleNavItem, simpleSectionOf } from "./simple-nav";

/** The Simple nav item of the page on screen, from the deepest matched route. */
export function useSimpleSection() {
  return useMatches({
    select: (matches) => {
      const leaf = matches.at(-1);
      return leaf === undefined ? undefined : simpleSectionOf(leaf.routeId);
    },
  });
}

// The nav marks the current item from the route (simple-nav.ts): a link is TanStack's active only
// on its exact location, so the two never disagree, as in Pro's nav.
const EXACT = { exact: true };

interface ItemProps {
  item: SimpleNavItem;
  active: boolean;
  teamId: string;
  brandId: string;
  onNavigate: (() => void) | undefined;
}

function NavItem({ item, active, teamId, brandId, onNavigate }: ItemProps) {
  const Icon = item.icon;
  const body = (
    <>
      <Icon size={18} strokeWidth={1.75} className={shell.navIcon} aria-hidden />
      <span>{item.label}</span>
    </>
  );
  // Pro's item, at the prototype's Simple size (9 px by 12 px, 14.5 px).
  const className = `${shell.navLink} ${classes.navLink}`;
  if (item.target.scope === "soon") {
    return (
      <Soon block>
        {(props) => (
          <button type="button" className={className} {...props}>
            {body}
          </button>
        )}
      </Soon>
    );
  }
  return (
    <Link
      to={item.target.to}
      params={{ teamId, brandId }}
      activeOptions={EXACT}
      className={className}
      data-active={active || undefined}
      aria-current={active ? "page" : undefined}
      onClick={onNavigate}
    >
      {body}
    </Link>
  );
}

/**
 * Simple's nav (spec ui-perf/simple-shell): the prototype's six items, the current page's marked,
 * then Create, which opens the picker (spec ui-perf/simple-create-picker), and Getting started at
 * its foot (spec ui-perf/simple-getting-started). Pro's frame draws it in the nav's slot, and in
 * its drawer on a phone.
 */
export function SimpleNav(props: { teamId: string; brandId: string; onNavigate?: (() => void) | undefined }) {
  const { teamId, brandId, onNavigate } = props;
  const section = useSimpleSection();
  const [picking, setPicking] = useState(false);
  return (
    <div className={classes.nav}>
      {SIMPLE_NAV.map((item: SimpleNavItem) => (
        <NavItem
          key={item.label}
          item={item}
          active={item.label === section}
          teamId={teamId}
          brandId={brandId}
          onNavigate={onNavigate}
        />
      ))}
      {/* The prototype's Create opens what can be made: its picker. A tile to the guided page
          starts that page anew, as Create did (spec ui-perf/simple-shell, decision 4). */}
      <button type="button" className={classes.create} aria-haspopup="dialog" onClick={() => setPicking(true)}>
        <Plus size={16} aria-hidden />
        Create
      </button>
      <CreatePicker
        teamId={teamId}
        brandId={brandId}
        opened={picking}
        onClose={() => setPicking(false)}
        onPick={() => {
          setPicking(false);
          onNavigate?.();
        }}
      />
      {/* The nav's foot, where the prototype docks it; in the drawer, under Create. */}
      <span className={classes.spacer} />
      <GettingStarted teamId={teamId} brandId={brandId} onNavigate={onNavigate} />
    </div>
  );
}
